import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { BookOpen, TrendingUp, Globe, Briefcase } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";

const translations = {
  overline: {
    es: "Por qué elegirnos",
    en: "Why Choose Us",
  },
  title: {
    es: "Impulsa a tu equipo y a tu organización",
    en: "Elevate your team and your organization",
  },
  benefits: {
    es: [
      {
        icon: BookOpen,
        title: "Programas a la medida",
        description: "Diseñamos contenidos formativos alineados a tus objetivos estratégicos, tu contexto local y el nivel de tu equipo.",
      },
      {
        icon: TrendingUp,
        title: "Impacto medible",
        description: "Definimos indicadores desde el inicio y damos seguimiento para asegurar transferencia real al puesto de trabajo.",
      },
      {
        icon: Globe,
        title: "Respaldo internacional",
        description: "Formación alineada con el marco educativo de PCMA Institute y las mejores prácticas globales de la industria.",
      },
      {
        icon: Briefcase,
        title: "Experiencia LATAM",
        description: "+14 años formando equipos en México, Colombia y El Salvador. Casos, ejemplos y metodologías propias del contexto latino.",
      },
    ],
    en: [
      {
        icon: BookOpen,
        title: "Tailored programs",
        description: "We design training content aligned with your strategic goals, local context, and the level of your team.",
      },
      {
        icon: TrendingUp,
        title: "Measurable impact",
        description: "We define KPIs from day one and follow up to ensure real knowledge transfer into the workplace.",
      },
      {
        icon: Globe,
        title: "International backing",
        description: "Training aligned with PCMA Institute's global education framework and the industry's best practices.",
      },
      {
        icon: Briefcase,
        title: "LATAM expertise",
        description: "+14 years training teams in Mexico, Colombia, and El Salvador. Cases, examples, and methodologies rooted in the Latin American context.",
      },
    ],
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const ValueSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  const benefits = t(translations.benefits);

  return (
    <section
      id="beneficios"
      data-testid="value-section"
      ref={ref}
      className="py-24 md:py-32 bg-[#EEF1F4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-flex items-center gap-2 text-[#0B1F3B] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
            {t(translations.overline)}
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
          </span>
          <h2
            data-testid="value-section-title"
            className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0B1F3B] font-medium tracking-tight"
          >
            {t(translations.title)}
          </h2>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8"
        >
          {benefits.map((benefit, index) => {
            const Icon = [BookOpen, TrendingUp, Globe, Briefcase][index];
            return (
              <motion.div
                key={index}
                variants={itemVariants}
                data-testid={`benefit-card-${index}`}
                className="group bg-white p-8 rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-gray-100"
              >
                <div className="w-14 h-14 bg-[#0B1F3B] rounded-lg flex items-center justify-center mb-6 group-hover:bg-[#2ED3C6] transition-colors duration-300">
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="font-display text-xl text-[#0B1F3B] font-medium mb-3">
                  {benefit.title}
                </h3>
                <p className="font-body text-[#0B1F3B]/70 text-base leading-relaxed">
                  {benefit.description}
                </p>
                <div className="mt-6 w-12 h-1 bg-[#F59E0B] group-hover:w-full transition-all duration-500" />
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default ValueSection;
